'use client'

import { VStack } from '@chakra-ui/react'
import { TagType } from 'ecosystem'
import { useMemo } from 'react'
import { getProductImages } from 'shared-utils/utils/products'
import { GrandHeading, GrandNextLink, GrandText, HoverableImages } from 'ui'
import {
  ProductBadges,
  ProductListItemBuy,
  ProductListItemProps
} from 'storefront-modules/products'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import { useCartQuantities } from 'storefront-modules/cart/hooks/useCartQuantities'
import { QuantityDiscountTeaser } from 'storefront-modules/products/components/QuantityDiscountTeaser'

export const CustomProductListItem = ({
  onBuy,
  onProductRequest,
  item,
  deliveryOptions,
  productsUrlPath,
  showsPrice,
  imageProps,
  imageAspectRatio: _imageAspectRatio,
  ...props
}: ProductListItemProps) => {
  const { productListItemImageAspectRatio } = useUIGlobalsContext()
  // Units already in the basket, bundled ones included — the map is memoised
  // in the selector, so a grid of cards walks the cart once between them.
  const cartQuantity = useCartQuantities().get(item.id) ?? 0
  const { mainImageUrl, secondaryImageUrl } = getProductImages(item.medias)
  const productBadges = useMemo(
    () => item.tags?.filter((tag) => tag.type === TagType.BADGE) || [],
    [item.tags]
  )

  const imageAspectRatio = _imageAspectRatio ?? productListItemImageAspectRatio ?? undefined

  return (
    <VStack
      _hover={{
        transform: {
          base: 'none',
          lg: 'translateY(-5px)'
        }
      }}
      bg="background.surface"
      borderRadius="sm"
      justifyContent="center"
      position="relative"
      py={4}
      spacing={1}
      transition="all 0.1s cubic-bezier(.17,.67,.83,.67)"
      {...props}
    >
      <GrandNextLink
        aria-label={item.name}
        h="full"
        href={`${productsUrlPath}/${item.slug}`}
        w="full"
      >
        <HoverableImages
          alt={item.name}
          badges={
            <ProductBadges
              badges={productBadges}
              imageProps={{
                sizes: `(min-width: 40em) 90px, 
                  (min-width: 30em) 80px,
                  60px
                `
              }}
              scalableSize={[60, 80, 90]}
            />
          }
          firstImgUrl={mainImageUrl}
          secondImgUrl={secondaryImageUrl || ''}
          title={item.name}
          imageProps={imageProps}
          aspectRatio={imageAspectRatio}
        />
      </GrandNextLink>

      <GrandHeading
        as="h3"
        fontSize={{
          base: 'sm',
          md: 'md'
        }}
        isTruncated
        pt={4}
        textAlign="center"
        title={item.name}
        tooltip={item.name}
        w="80%"
      />
      <GrandText
        fontSize={{
          base: 'xs',
          md: 'sm'
        }}
        py={3}
        textAlign="center"
        w="80%"
      >
        {item.articleNumber}
      </GrandText>

      {/* Same placement and cart-awareness as the shared `ProductListItem`, which
          this component is a visual fork of. */}
      <QuantityDiscountTeaser
        px={4}
        product={item}
        quantity={cartQuantity}
        surface="listItem"
        textAlign="center"
        w="80%"
      />

      {showsPrice ? (
        <ProductListItemBuy
          price={{
            price: item.price,
            originalPrice: item.originalPrice,
            priceWithoutVat: item.priceWithoutVat,
            originalPriceWithoutVat: item.originalPriceWithoutVat
          }}
          product={item}
          {...{ deliveryOptions, onBuy, onProductRequest, productsUrlPath }}
        />
      ) : null}
    </VStack>
  )
}
